// @erp/module-cms/fe · the query-key factory (DEV-GUIDE-002 §3.5 — ad-hoc string
// keys in components are BANNED: targeted invalidation dies on a typo). Every
// query and every mutation's invalidateQueries reads its key from here, so a
// rename can never silently desync a screen's cache. Mirrors
// modules/lib/src/fe/keys.ts.
//
// Shape: hierarchical tuples rooted at ['cms'], one branch per routed domain —
//   ['cms']                                → cmsKeys.all                 (nuke everything)
//   ['cms','announcements']                → cmsKeys.announcements.all() (a whole domain)
//   ['cms','announcements','list', f]      → cmsKeys.announcements.list(f)
//   ['cms','announcements','detail', id]   → cmsKeys.announcements.detail(id)
// Prefix-invalidation is the point: invalidating ['cms','announcements'] sweeps
// every list page AND every detail under it, because TanStack matches by prefix.
//
// Canonical property order (DEV-GUIDE-002 §8.3): TanStack's hashKey sorts object
// keys, so a filters object hashes identically regardless of insertion order —
// but callers should still build filters in one order to keep devtools readable.
//
// Filter params stay GENERIC (`<F>`): the concrete schema query type flows in at
// the call site (queries.ts passes CmsAnnouncementListQuery), so the key is
// precise without this file importing the domains' query contracts.
//
// SCOPE: announcements + tenders + events + publications + publication titles + gallery (each the same
// list<F>/detail shape) + media (the asset library — the same list<F>/detail shape,
// but the detail leaf is the access-gated SERVE, not a plain row read) + categories
// + settings + newsletter subscribers (a list<F>-ONLY branch — admin list + hard
// delete, no detail read) + revisions (the per-entity History log — keyed by the
// (type, id) pair the panel reads, NOT one of the content domains) — the CMS admin surface.
import type { RevisionEntityType } from "../schemas/index.js";

/** The one root. Everything below composes from it; `all` alone invalidates the module. */
const ROOT = ["cms"] as const;

export const cmsKeys = {
  all: ROOT,

  /* The CONSOLE's figures (GET /stats, 2026-10-05) — the home's own leaf, NOT under
     any one domain. It counts six sections at once, so no single `<domain>.all()`
     sweep owns it; a write to announcements must still refresh it. It sits directly
     under ROOT, which every domain's invalidate is a prefix of, so the ordinary
     `cmsKeys.all` sweep that follows a write carries it along. */
  consoleStats: () => [...ROOT, "console-stats"] as const,

  // ── Announcements (the pattern-setting content type — keyset list + detail,
  //    soft-delete/restore/purge lifecycle). A published-feed public read-split
  //    arrives with the website wave; it will live on its OWN sibling leaf then
  //    (the lib opac.* precedent), never sharing a cache entry with this admin
  //    list. `bySlug(slug, locale)` is the translation affordance's sibling lookup
  //    (GET /<type>/by-slug/:slug?locale=) — a THIRD leaf under the domain prefix, so
  //    the create that mints the sibling sweeps it with the same <domain>.all()
  //    invalidate and the form flips from "Translate" to "Open" without a hand-patch.
  //    The same leaf exists on events / publications / gallery. `stats()` (2026-09-22) keys the
  //    GET /<type>/stats status counts the list's stat tiles read — a FOURTH leaf under the same
  //    prefix, so every write that sweeps <domain>.all() (publish, trash, bulk, restore) refreshes
  //    the figures with the page; no mutation names it. On every content domain + subscribers. ──
  announcements: {
    all: () => [...ROOT, "announcements"] as const,
    list: <F>(filters: F) => [...ROOT, "announcements", "list", filters] as const,
    detail: (id: string) => [...ROOT, "announcements", "detail", id] as const,
    stats: () => [...ROOT, "announcements", "stats"] as const,
    bySlug: (slug: string, locale: string) => [...ROOT, "announcements", "by-slug", slug, locale] as const,
  },

  // ── Tenders (the richest content type — keyset list + detail, a status lifecycle
  //    and soft-delete/restore/purge trash; the exact announcements shape). A
  //    corrigendum lives UNDER its parent via the ?parentId list filter, still on
  //    this one branch. A tender write ALSO upserts/hides a companion announcement
  //    server-side (0005 auto-sync) — that cross-domain sweep is left to the caller
  //    (the settings precedent), never fanned out from this branch. ──
  tenders: {
    all: () => [...ROOT, "tenders"] as const,
    list: <F>(filters: F) => [...ROOT, "tenders", "list", filters] as const,
    detail: (id: string) => [...ROOT, "tenders", "detail", id] as const,
    stats: () => [...ROOT, "tenders", "stats"] as const,
  },

  // ── Events (the calendar content type — keyset list + detail, status lifecycle,
  //    soft-delete/restore/purge). The ?featured / ?upcoming narrowings ride the
  //    list filters, so they stay within this branch. Companion-announcement sync is
  //    the tenders precedent (caller-driven cross-domain sweep). ──
  events: {
    all: () => [...ROOT, "events"] as const,
    list: <F>(filters: F) => [...ROOT, "events", "list", filters] as const,
    detail: (id: string) => [...ROOT, "events", "detail", id] as const,
    stats: () => [...ROOT, "events", "stats"] as const,
    bySlug: (slug: string, locale: string) => [...ROOT, "events", "by-slug", slug, locale] as const,
  },

  // ── Publications (the document library — keyset list + detail, status lifecycle,
  //    soft-delete/restore/purge). The ?docType / ?activeStatus narrowings ride the
  //    list filters. Companion-announcement sync is the tenders precedent. ──
  publications: {
    all: () => [...ROOT, "publications"] as const,
    list: <F>(filters: F) => [...ROOT, "publications", "list", filters] as const,
    detail: (id: string) => [...ROOT, "publications", "detail", id] as const,
    stats: () => [...ROOT, "publications", "stats"] as const,
    bySlug: (slug: string, locale: string) => [...ROOT, "publications", "by-slug", slug, locale] as const,
  },

  // ── Publication titles (db-cms 0022 — the periodical / series a publication belongs
  //    to; keyset list + detail + by-slug, status lifecycle, soft-delete/restore/purge). A
  //    title write ALSO changes every linked publication's `publicationTitle` ref on the
  //    public wire, but the ADMIN publication DTO carries only `titleId`, so no cross-domain
  //    sweep is owed here (the issue form re-reads the title by id through detail()). ──
  publicationTitles: {
    all: () => [...ROOT, "publication-titles"] as const,
    list: <F>(filters: F) => [...ROOT, "publication-titles", "list", filters] as const,
    detail: (id: string) => [...ROOT, "publication-titles", "detail", id] as const,
    stats: () => [...ROOT, "publication-titles", "stats"] as const,
    bySlug: (slug: string, locale: string) => [...ROOT, "publication-titles", "by-slug", slug, locale] as const,
  },

  // ── Gallery (the photo library, the LAST content type — keyset list + detail,
  //    status lifecycle, soft-delete/restore/purge). NOT a companion-announcement
  //    target (a photo does not post to the notice feed), so a gallery write sweeps
  //    ONLY this branch. ──
  gallery: {
    all: () => [...ROOT, "gallery"] as const,
    list: <F>(filters: F) => [...ROOT, "gallery", "list", filters] as const,
    detail: (id: string) => [...ROOT, "gallery", "detail", id] as const,
    stats: () => [...ROOT, "gallery", "stats"] as const,
    bySlug: (slug: string, locale: string) => [...ROOT, "gallery", "by-slug", slug, locale] as const,
  },

  // ── Media (the asset LIBRARY behind the presign UPLOAD flow — keyset list + a
  //    per-row serve detail. NOT a lifecycle content type (no status/trash): a
  //    "create" is the uploadMedia FLOW (presign → PUT → complete) and a delete is
  //    HARD. The detail leaf is the access-gated SERVE — media.detail(id) keys the
  //    GET /media/:id read that mints the short-lived signed url the picker renders.
  //    Every write (upload/delete) sweeps media.all() by prefix, covering every
  //    list-filter permutation AND the serve leaf in one call. ──
  media: {
    all: () => [...ROOT, "media"] as const,
    list: <F>(filters: F) => [...ROOT, "media", "list", filters] as const,
    detail: (id: string) => [...ROOT, "media", "detail", id] as const,
  },

  // ── Categories (the per-content-TYPE taxonomy — a bare array keyed by the
  //    optional ?type filter; null ⇒ every type — and, since the Hindi wave, the
  //    optional ?locale filter; null ⇒ every locale, which is what the forms' pickers
  //    and the sibling lookup read). A type rename/re-point can move a row between
  //    type-filtered lists, so category writes sweep categories.all() (the granularity
  //    this branch offers). detail is future-proofing (the API exposes GET
  //    /categories/:id) even though CE1 reads categories find-in-list. ──
  categories: {
    all: () => [...ROOT, "categories"] as const,
    list: (type?: string, locale?: string) => [...ROOT, "categories", "list", type ?? null, locale ?? null] as const,
    detail: (id: string) => [...ROOT, "categories", "detail", id] as const,
  },

  // ── Site-wide settings (key/value merged over DEFAULT_SETTINGS). Two leaves:
  //    the admin FULL config (rides cms:settings) and the PUBLIC allowlisted
  //    subset (anonymous, sensitive keys stripped — the lib opac.settings
  //    precedent). `public` is nested UNDER settings so a settings write's
  //    invalidate(settings.all()) sweeps both leaves in one prefix match. ──
  settings: {
    all: () => [...ROOT, "settings"] as const,
    public: () => [...ROOT, "settings", "public"] as const,
  },

  // ── Newsletter subscribers (LOCAL capture behind the static site's signup form —
  //    db-cms 0009). The admin surface only LISTS and hard-DELETES; subscribe/
  //    unsubscribe are the WEBSITE's anonymous public writes (never an admin hook), so
  //    there is no create/update leaf. And NO detail leaf either — the API exposes only
  //    GET /newsletter/subscribers (the keyset list) + DELETE /newsletter/subscribers/:id;
  //    a row is read in the list, never fetched singly. A delete sweeps subscribers.all()
  //    by prefix, covering every ?status/?search list permutation in one call. ──
  subscribers: {
    all: () => [...ROOT, "subscribers"] as const,
    list: <F>(filters: F) => [...ROOT, "subscribers", "list", filters] as const,
    stats: () => [...ROOT, "subscribers", "stats"] as const,
  },

  // ── Cookies / consent registry (SITE CONFIG behind the static site's consent
  //    banner — db-cms 0010). Not a lifecycle content type: no status/trash and no
  //    slug — a create/update/delete is a plain CRUD write over a small registry, so
  //    a delete is HARD. The admin surface LISTS (optionally narrowed by ?category)
  //    and does full CRUD; the public consent payload (grouped by category) is the
  //    WEBSITE's anonymous read, never an admin hook, so there is no leaf for it here.
  //    NO detail leaf — the API exposes GET /cookies/:id, but the admin edits a row
  //    read from the list (the subscribers list-only precedent, but a full CRUD lane).
  //    Every write sweeps cookies.all() by prefix, covering every ?category list
  //    permutation in one call. ──
  cookies: {
    all: () => [...ROOT, "cookies"] as const,
    list: <F>(filters: F) => [...ROOT, "cookies", "list", filters] as const,
  },

  // ── Revisions (the append-only History log for the four rich content types —
  //    announcement | tender | event | publication; db-cms 0013). A revision is
  //    NOT a domain of its own: it is a SNAPSHOT log HANGING OFF one content row,
  //    so a revision branch is keyed by the (type, id) pair the History panel
  //    reads. Leaves:
  //      list(type, id)          → the metadata log for one record's History panel
  //      detail(type, id, revNo) → one snapshot's read-only View
  //    A restore writes a fresh revision (and mutates the record), so the restore
  //    hook sweeps revisions.all() (this whole branch, every record's log) PLUS
  //    the affected content domain — the revisions branch never fans a content
  //    sweep out itself (the settings/companion caller-driven precedent). ──
  revisions: {
    all: () => [...ROOT, "revisions"] as const,
    list: (type: RevisionEntityType, id: string) => [...ROOT, "revisions", "list", type, id] as const,
    detail: (type: RevisionEntityType, id: string, revNo: number) =>
      [...ROOT, "revisions", "detail", type, id, revNo] as const,
  },

  // ── The reader (GET /reading/:slug — the reading gate, db-cms 0024). The MEMBER
  //    portal's one CMS read: a public publication DTO + a 15-minute presigned file,
  //    keyed by (slug, locale). Its OWN branch, never `publications.bySlug` — that leaf
  //    is the admin translate affordance's live-row lookup (any status), and the two
  //    must never share a cache entry: this one carries a signed URL minted for ONE
  //    viewer and answers 401/403 by session, so a sign-out invalidates reading.all(). ──
  reading: {
    all: () => [...ROOT, "reading"] as const,
    bySlug: (slug: string, locale: string | undefined) => [...ROOT, "reading", slug, locale ?? null] as const,
  },
} as const;

export type CmsKeys = typeof cmsKeys;
